<script setup lang="ts">
import { NavActivity } from '../types/navigation'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useTimers } from '../store/timers'
import { Timer } from '../types/timers'
import FormHeader from './FormHeader.vue'
import FormTravel from './FormTravel.vue'
import FormParentActivity from './FormParentActivity.vue'
import { useI18n } from 'vue-i18n'
import { useGetOdometerReading } from '../composables/getOdometerReading'

const props = withDefaults(
  defineProps<{
    /** Used to determine the type of activity*/
    modalType: NavActivity
    /** Used for timeline inserting activity */
    insertData: Timer
  }>(),
  {
    insertData: undefined,
  }
)
const emit = defineEmits<{
  (e: 'toggleDrawer'): void
  (e: 'closeModal'): void
  (e: 'toggleTravelActive'): void
}>()
//translations
const { t } = useI18n({ useScope: 'global' })
const label = (key: string) => t(`form.${key}`)

const { getActiveParentTimer, getAllParentTimers } = storeToRefs(useTimers())

//start odometer default for new
const defaultStartOdo = computed(() => useGetOdometerReading().previous.value)

const activeType = (type: Timer['type']) => {
  return getActiveParentTimer.value?.type === type
}

//emits
const toggleNav = () => {
  emit('toggleDrawer')
}
const toggleTravelActive = () => {
  emit('toggleTravelActive')
}
const closeModal = () => {
  emit('closeModal')
}

//template logic
const activeButDifferentTimerType = computed(
  () =>
    getActiveParentTimer.value &&
    !activeType(props.modalType.type) &&
    typeof props.insertData === 'undefined'
)
const typeIsTravel = computed(() => props.modalType.type === 'travel')
const typeIsNotStore = computed(() => props.modalType.type !== 'store')
const typeIsStore = computed(() => props.modalType.type === 'store')
//form values
const isInsert = computed(() => typeof props.insertData !== 'undefined')
const insertTimes = computed(() => {
  if (isInsert.value) {
    return { start: props.insertData!.start_time, end: props.insertData!.end_time }
  } else {
    return undefined
  }
})
//Form travel values
const isInsertTravel = computed(() => isInsert.value || incompleteTravel.value)
const incompleteTravel = computed(() => {
  return getAllParentTimers.value.length > 0
    ? getAllParentTimers.value[getAllParentTimers.value.length - 1].data.incomplete
    : false
})
const travelIndex = computed(() => {
  return incompleteTravel.value ? getAllParentTimers.value.length - 1 : undefined
})
const insertTimesTravel = computed(() => {
  return incompleteTravel.value
    ? {
        start: getAllParentTimers.value[getAllParentTimers.value.length - 1].start_time,
        end: getAllParentTimers.value[getAllParentTimers.value.length - 1].end_time
          ? getAllParentTimers.value[getAllParentTimers.value.length - 1].end_time
          : new Date(),
      }
    : insertTimes.value
})

const insertOdo = computed(() => {
  return incompleteTravel.value
    ? {
        startOdo: getAllParentTimers.value[getAllParentTimers.value.length - 1].data.odometer_start,
        endOdo: getAllParentTimers.value[getAllParentTimers.value.length - 1].data.odometer_end,
      }
    : {
        startOdo: isInsert.value ? props.insertData!.data.odometer_start : defaultStartOdo.value,
        endOdo: isInsert.value ? props.insertData!.data.odometer_end : undefined,
      }
})
const locations = computed(() => {
  return incompleteTravel.value
    ? {
        departing: getAllParentTimers.value[getAllParentTimers.value.length - 1].data.departing,
        departingStoreName:
          getAllParentTimers.value[getAllParentTimers.value.length - 1].data.departingStoreName,
        departingStoreNumber:
          getAllParentTimers.value[getAllParentTimers.value.length - 1].data.departingStoreNumber,
        destination: getAllParentTimers.value[getAllParentTimers.value.length - 1].data.destination,
        destinationStoreName:
          getAllParentTimers.value[getAllParentTimers.value.length - 1].data.destinationStoreName,
        destinationStoreNumber:
          getAllParentTimers.value[getAllParentTimers.value.length - 1].data.destinationStoreNumber,
      }
    : props.insertData?.data
})
</script>

<template>
  <q-card style="min-width: 280px" class="q-pa-md">
    <!-- Header -->
    <form-header :type="modalType" />

    <!-- Forms -->
    <!-- Form content -->
    <template v-if="activeButDifferentTimerType">
      <div class="row q-gutter-sm q-my-sm">
        <!-- End current activity -->
        <q-card-section class="q-pt-none">
          {{ t('form.endCurrentMessage') }}
        </q-card-section>
      </div>
      <!-- Close button -->
      <q-card-actions align="right" class="text-primary">
        <q-btn flat :label="label('close')" v-close-popup />
      </q-card-actions>
    </template>
    <template v-else-if="typeIsTravel">
      <form-travel
        :index="travelIndex"
        :insert="isInsertTravel"
        :insert-times="insertTimesTravel"
        :insert-odo="insertOdo"
        :locations="locations"
        @saved="toggleTravelActive(), closeModal(), toggleNav()"
        @started="toggleTravelActive()"
        @cancel="closeModal()"
        @close="closeModal(), toggleNav()"
      />
    </template>
    <template v-else-if="typeIsNotStore">
      <form-parent-activity
        :index="activeType(modalType.type) ? getAllParentTimers.length - 1 : undefined"
        :insert="isInsert"
        :insert-times="insertTimes"
        :end-state="typeof insertData !== 'undefined' ? true : activeType(modalType.type)"
        :activity-type="modalType.type"
        @cancel="closeModal(), toggleNav()"
        @started="closeModal(), toggleNav()"
        @close="closeModal(), toggleNav()"
      />
    </template>
    <template v-else-if="typeIsStore">
      {{ label('promptStoreVisit') }}
    </template>
  </q-card>
</template>
